import { useState } from "react";
import Modal from "@/components/ui/Modal";
import SessionForm from "./SessionForm";
import { useCreateSession, useUpdateSession } from "@/hooks/useSessions";
import { useTranslation } from "react-i18next";
import type { Session, CreateSessionRequest } from "@/types";
import { handleApiError, type ApiError } from "@/utils/errorHandler";

interface SessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  session?: Session;
}

export default function SessionModal({
  isOpen,
  onClose,
  session,
}: SessionModalProps) {
  const { t } = useTranslation("pages");
  const [error, setError] = useState("");

  const createSession = useCreateSession();
  const updateSession = useUpdateSession();

  const isEditing = !!session;
  const isLoading = createSession.isPending || updateSession.isPending;

  const handleSubmit = async (data: CreateSessionRequest) => {
    setError("");

    try {
      if (isEditing) {
        await updateSession.mutateAsync({ id: session.id, ...data });
      } else {
        await createSession.mutateAsync(data);
      }
      onClose();
    } catch (err: unknown) {
      const errorMessage = handleApiError(err as ApiError);
      setError(errorMessage);
    }
  };

  const handleClose = () => {
    setError("");
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        isEditing
          ? t("sessions.formModal.editTitle")
          : t("sessions.formModal.createTitle")
      }
      size="lg"
    >
      {error && (
 <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Only render form when we have session data for editing, or when creating */}
      {(!isEditing || session) && (
        <SessionForm
          key={session?.id} // Force re-mount when session changes
          onSubmit={handleSubmit}
          onCancel={handleClose}
          initialData={session}
          isLoading={isLoading}
          mode={isEditing ? "edit" : "create"}
        />
      )}

      {/* Show loading state when editing but session data is not available */}
      {isEditing && !session && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-secondary-600">
            {t("sessions.formModal.loadingData")}
          </p>
        </div>
      )}
    </Modal>
  );
}
